<template>
  <span ref="body" class="wt-svg-animation" :class="'-name-' + name">
    <slot />
  </span>
</template>

<script>
/**
 * API
 * @link https://github.com/airbnb/lottie-web
 * @link http://airbnb.io/lottie/web/getting-started.html
 *
 * Preview
 * @link https://www.lottiefiles.com/preview
 *
 * More examples
 * @link https://www.lottiefiles.com/?page=1
 *
 * @example
 *
 * <wt-svg-animation name="bug" :play="false"/>
 */

import player from 'lottie-web';
import BugJson from './animation/bug.json';
import BugsJson from './animation/bugs.json';
import SearchJson from './animation/search.json';
import LightboxClapperJson from './animation/lightbox-clapper.json';
import UploadFolderJson from './animation/upload-folder.json';

const config = {
  bug: {
    data: BugJson,
    autoplay: false,
    loop: true,
  },

  bugs: {
    data: BugsJson,
    autoplay: true,
    loop: true,
  },

  'light-box-search': {
    data: SearchJson,
    autoplay: false,
    loop: false,
    scene: {
      // property names are subject to specific animation convention
      mounted: { stop: 0 },
      mouseenter: { play: [0, 70], forceFlag: true, loop: false },
      mouseleave: { play: [70, 130], forceFlag: true, loop: false },
    },
  },

  'lightbox-clapper': {
    data: LightboxClapperJson,
    autoplay: false,
    loop: false,
    scene: {
      mounted: { stop: 0 },
      added: { play: [0, 129], forceFlag: false, loop: false },
      fludded: { play: [130, 200], forceFlag: true, loop: true },
    },
  },

  'upload-folder': {
    data: UploadFolderJson,
    autoplay: false,
    loop: true,
  },
};

for (const animName in config) {
  if (Object.getOwnPropertyDescriptor(config, animName)) {
    const anim = config[animName];
    if (typeof anim.initialize === 'function') {
      anim.initialize(anim.data);
    }
  }
}

export default {
  props: {
    name: { type: String, required: true },
    play: { type: [Boolean, Object], default: null },
    scene: { type: [String], default: null },
  },

  beforeDestroy() {
    this.$_destroyPlayer();
  },

  mounted() {
    this.$_initPlayer();

    if (this.play !== null && this.play !== undefined) {
      this.applyPlayChange();
    }

    if (this.scene !== null && this.scene !== undefined) {
      this.applySceneChange();
    }
  },

  watch: {
    name() {
      if (this.$options.player) {
        this.applyNameChange();
      }
    },

    play() {
      if (this.$options.player) {
        this.applyPlayChange();
      }
    },
    scene() {
      if (this.$options.player) {
        this.applySceneChange();
      }
    },
  },

  computed: {
    animation() {
      return config[this.name];
    },
  },

  methods: {
    $_destroyPlayer() {
      this.$options.player.destroy();
      this.$options.player = null;
    },

    $_initPlayer() {
      this.$options.player = player.loadAnimation({
        container: this.$refs.body,
        renderer: 'svg',
        animationData: this.animation.data,
        loop: this.animation.loop,
        autoplay: this.animation.autoplay,
        rendererSettings: this.animation.rendererSettings,
      });
    },

    applyNameChange() {
      this.$_destroyPlayer();
      this.$_initPlayer();
    },

    applyPlayChange() {
      if (this.play) {
        this.$options.player.play();
      } else {
        this.$options.player.stop();
      }
    },

    applySceneChange() {
      const scene = this.animation.scene[this.scene];
      // console.log(this.scene);

      if (scene === null || scene === undefined) {
        throw new Error(`Undefined scene name: ${scene}`);
      }

      if (scene.stop !== null && scene.stop !== undefined) {
        this.$options.player.loop = false;
        // there is a problem of showing single frame AFTER playing segments
        if (this.$options.player.firstFrame !== 0) {
          // play single frame as a segment
          const segment = [scene.stop, scene.stop + 1];
          this.$options.player.playSegments(segment, true);
        } else {
          this.$options.player.goToAndStop(scene.stop, true);
        }
      } else if (scene.play !== null && scene.play !== undefined) {
        this.$options.player.loop = scene.loop;
        this.$options.player.playSegments(scene.play, scene.forceFlag);
      }
    },
  },
};
</script>
